
import { useToast } from "@/hooks/use-toast";
import { Conversation } from "@/types/conversation";
import { DBMessage } from "@/types/message";
import type { ClarificationType } from "@/components/refund/ClarificationInput";

interface ConversationHandlersProps {
  conversation: Conversation | null;
  messages: DBMessage[];
  handleGenerateResponse: (sellerResponseContent?: string) => Promise<any>;
  setIsClarificationSubmitting: (submitting: boolean) => void;
}

export const useConversationHandlers = ({
  conversation,
  messages,
  handleGenerateResponse,
  setIsClarificationSubmitting
}: ConversationHandlersProps) => {
  const { toast } = useToast();

  // Handle clarification submission - simplified to just pass the clarification text
  const handleClarificationSubmit = async (clarification: string, type: ClarificationType) => {
    if (!conversation || !messages.length) return;

    setIsClarificationSubmitting(true);
    
    try {
      // Generate new response with clarification - the AI will understand the intent from the text
      const result = await handleGenerateResponse(clarification);
      
      if (result) {
        toast({
          title: "Response updated",
          description: "AI response has been regenerated with your clarification.",
        });
      }
    } catch (error: any) {
      console.error("Error applying clarification:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to apply clarification. Please try again.",
      });
    } finally {
      setIsClarificationSubmitting(false);
    }
  };

  // Handle follow-up generation
  const handleGenerateFollowUp = async (type: string) => {
    if (!conversation) return;

    try {
      // Generate follow-up based on type
      const followUpContent = `Generate ${type} message`;
      await handleGenerateResponse(followUpContent);
      
      toast({
        title: "Follow-up generated",
        description: `${type.replace('_', ' ')} message has been generated.`,
      });
    } catch (error: any) {
      console.error("Error generating follow-up:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate follow-up. Please try again.",
      });
    }
  };

  return {
    handleClarificationSubmit,
    handleGenerateFollowUp
  };
};
